// @ts-nocheck
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import ContatoModal from '@/components/ContatoModal';
import LocalModal from '@/components/LocalModal';

type FornecedorFormData = {
  cnpj: string;
  razao_social: string;
  apelido: string;
  inscricao_estadual: string;
  inscricao_municipal: string;
  email_nfe: string;
  fora_municipio: boolean;
  ibge_municipio: string;
  limite_credito: number;
  matricula_cei_cno: string;
  agencia: string;
  conta: string;
  pix: string;
  banco: string;
  cnpj_bancario: string;
};

type Local = {
  id: string;
  nome: string;
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
};

type Contato = {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  cargo: string;
};

export default function FornecedorForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [locais, setLocais] = useState<Local[]>([]);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [fornecedorLocais, setFornecedorLocais] = useState<string[]>([]);
  const [fornecedorContatos, setFornecedorContatos] = useState<string[]>([]);

  const isEditing = !!id;
  const canWrite = hasPermission('clientes', 'fornecedores', 'write');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FornecedorFormData>();

  const foraMunicipal = watch('fora_municipio');

  useEffect(() => {
    loadLocais();
    loadContatos();
    if (isEditing) {
      loadFornecedor();
    }
  }, [id]);

  const loadLocais = async () => {
    try {
      const { data, error } = await supabase
        .from('locais')
        .select('*')
        .eq('ativo', true)
        .order('nome');

      if (error) throw error;
      setLocais(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar locais:', error);
    }
  };

  const loadContatos = async () => {
    try {
      const { data, error } = await supabase
        .from('contatos')
        .select('*')
        .eq('ativo', true)
        .order('nome');

      if (error) throw error;
      setContatos(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar contatos:', error);
    }
  };

  const loadFornecedor = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('fornecedores')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        Object.keys(data).forEach((key) => {
          if (key in data && data[key] !== null) {
            setValue(key as keyof FornecedorFormData, data[key]);
          }
        });

        // Carregar locais relacionados
        const { data: locaisData } = await supabase
          .from('locais')
          .select('id')
          .eq('id_fornecedor', id);

        if (locaisData) {
          setFornecedorLocais(locaisData.map(l => l.id));
        }

        // Carregar contatos relacionados
        const { data: contatosData } = await supabase
          .from('contatos')
          .select('id')
          .eq('id_fornecedor', id);

        if (contatosData) {
          setFornecedorContatos(contatosData.map(c => c.id));
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar fornecedor:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do fornecedor: " + error.message,
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: FornecedorFormData) => {
    if (!canWrite) {
      toast({
        title: "Erro",
        description: "Você não tem permissão para salvar fornecedores.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const fornecedorData = {
        ...data,
        created_by: isEditing ? undefined : (await supabase.auth.getUser()).data.user?.id,
        updated_by: (await supabase.auth.getUser()).data.user?.id,
      };

      let fornecedorId = id;

      if (isEditing) {
        const { error } = await supabase
          .from('fornecedores')
          .update(fornecedorData)
          .eq('id', id);

        if (error) throw error;
      } else {
        const { data: newFornecedor, error } = await supabase
          .from('fornecedores')
          .insert([{
            ...fornecedorData,
            nome: fornecedorData.razao_social // Mapear razao_social para nome
          }])
          .select()
          .single();

        if (error) throw error;
        fornecedorId = newFornecedor.id;
      }

      // Atualizar relacionamentos com locais
      if (fornecedorId) {
        await supabase
          .from('locais')
          .update({ id_fornecedor: null })
          .eq('id_fornecedor', fornecedorId);

        if (fornecedorLocais.length > 0) {
          await supabase
            .from('locais')
            .update({ id_fornecedor: fornecedorId })
            .in('id', fornecedorLocais);
        }

        // Atualizar relacionamentos com contatos
        await supabase
          .from('contatos')
          .update({ id_fornecedor: null })
          .eq('id_fornecedor', fornecedorId);

        if (fornecedorContatos.length > 0) {
          await supabase
            .from('contatos')
            .update({ id_fornecedor: fornecedorId })
            .in('id', fornecedorContatos);
        }
      }

      toast({
        title: "Sucesso",
        description: `Fornecedor ${isEditing ? 'atualizado' : 'criado'} com sucesso!`,
      });

      navigate('/intranet/fornecedores');
    } catch (error: any) {
      console.error('Erro ao salvar fornecedor:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar fornecedor: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canWrite) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Você não tem permissão para {isEditing ? 'editar' : 'criar'} fornecedores.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/intranet/fornecedores')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Editar Fornecedor' : 'Novo Fornecedor'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Atualize os dados do fornecedor' : 'Cadastre um novo fornecedor'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Tabs defaultValue="dados-basicos" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
            <TabsTrigger value="endereco">Locais</TabsTrigger>
            <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
            <TabsTrigger value="contatos">Contatos</TabsTrigger>
            <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
          </TabsList>

          <TabsContent value="dados-basicos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Dados Básicos</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    {...register('cnpj', { required: 'CNPJ é obrigatório' })}
                    placeholder="00.000.000/0001-00"
                  />
                  {errors.cnpj && (
                    <p className="text-sm text-destructive">{errors.cnpj.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="razao_social">Razão Social *</Label>
                  <Input
                    id="razao_social"
                    {...register('razao_social', { required: 'Razão Social é obrigatória' })}
                    placeholder="Nome da empresa"
                  />
                  {errors.razao_social && (
                    <p className="text-sm text-destructive">{errors.razao_social.message}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="apelido">Apelido</Label>
                  <Input
                    id="apelido"
                    {...register('apelido')}
                    placeholder="Nome fantasia"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="endereco" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Locais Vinculados</CardTitle>
                  <LocalModal 
                    onLocalCriado={loadLocais}
                    trigger={
                      <Button variant="outline" size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Novo Local
                      </Button>
                    }
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {locais.map((local) => (
                    <div key={local.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`local-${local.id}`}
                        checked={fornecedorLocais.includes(local.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFornecedorLocais([...fornecedorLocais, local.id]);
                          } else {
                            setFornecedorLocais(fornecedorLocais.filter(id => id !== local.id));
                          }
                        }}
                      />
                      <Label htmlFor={`local-${local.id}`} className="text-sm">
                        {local.nome} - {local.logradouro}, {local.numero} - {local.cidade}/{local.estado}
                      </Label>
                    </div>
                  ))}
                  {locais.length === 0 && (
                    <p className="text-muted-foreground">Nenhum local cadastrado.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fiscal" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Informações Fiscais</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inscricao_estadual">Inscrição Estadual</Label>
                  <Input
                    id="inscricao_estadual"
                    {...register('inscricao_estadual')}
                    placeholder="123.456.789.000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inscricao_municipal">Inscrição Municipal</Label>
                  <Input
                    id="inscricao_municipal"
                    {...register('inscricao_municipal')}
                    placeholder="123456789"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_nfe">E-mail NFe</Label>
                  <Input
                    id="email_nfe"
                    type="email"
                    {...register('email_nfe')}
                    placeholder="nfe@empresa.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ibge_municipio">IBGE Município</Label>
                  <Input
                    id="ibge_municipio"
                    {...register('ibge_municipio')}
                    placeholder="1234567"
                  />
                </div>

                <div className="flex items-center space-x-2 md:col-span-2">
                  <Checkbox
                    id="fora_municipio"
                    {...register('fora_municipio')}
                  />
                  <Label htmlFor="fora_municipio">Fora do Município</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contatos" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Contatos Associados</CardTitle>
                  <ContatoModal 
                    onContatoCriado={loadContatos}
                    trigger={
                      <Button variant="outline" size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Novo Contato
                      </Button>
                    }
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {contatos.map((contato) => (
                    <div key={contato.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`contato-${contato.id}`}
                        checked={fornecedorContatos.includes(contato.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFornecedorContatos([...fornecedorContatos, contato.id]);
                          } else {
                            setFornecedorContatos(fornecedorContatos.filter(id => id !== contato.id));
                          }
                        }}
                      />
                      <Label htmlFor={`contato-${contato.id}`} className="text-sm">
                        {contato.nome} - {contato.email} - {contato.telefone}
                        {contato.cargo && ` (${contato.cargo})`}
                      </Label>
                    </div>
                  ))}
                  {contatos.length === 0 && (
                    <p className="text-muted-foreground">Nenhum contato cadastrado.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="financeiro" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Informações Financeiras</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="limite_credito">Limite de Crédito</Label>
                    <Input
                      id="limite_credito"
                      type="number"
                      step="0.01"
                      {...register('limite_credito', { valueAsNumber: true })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="matricula_cei_cno">Matrícula CEI/CNO</Label>
                    <Input
                      id="matricula_cei_cno"
                      {...register('matricula_cei_cno')}
                      placeholder="123456789"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Dados Bancários de Recebimento</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="banco">Banco</Label>
                      <Input
                        id="banco"
                        {...register('banco')}
                        placeholder="Nome do banco"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="agencia">Agência</Label>
                      <Input
                        id="agencia"
                        {...register('agencia')}
                        placeholder="1234"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="conta">Conta</Label>
                      <Input
                        id="conta"
                        {...register('conta')}
                        placeholder="12345-6"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cnpj_bancario">CNPJ</Label>
                      <Input
                        id="cnpj_bancario"
                        {...register('cnpj_bancario')}
                        placeholder="00.000.000/0001-00"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="pix">PIX</Label>
                      <Input
                        id="pix"
                        {...register('pix')}
                        placeholder="Chave PIX"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/intranet/fornecedores')}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isEditing ? 'Atualizar' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}